<template>
  <div>
    <BlBackdrop
      title="Invite Member"
      description="Please fill in the following information to invite a new member."
      :footer="false"
    >
      <div class="flex flex-col gap-4 pb-8 px-8">
        <div class="grid grid-cols-3">
          <div
            v-for="progress in invitationProgress"
            :key="progress.step"
            :class="
              progress.step <= currentStep
                ? 'border-light-purple'
                : 'border-light-purple-100'
            "
            class="flex flex-col gap-1 pb-1 border-b-4"
          >
            <BlTextBody3 :text="`Step ${progress.step}:`" />
            <BlTextSubtitle2
              :class="{ 'text-grey-400': progress.step > currentStep }"
              :text="progress.label"
            />
          </div>
        </div>

        <form
          v-if="currentStep === 1"
          class="flex flex-col gap-4"
          @submit.prevent="currentStep = 2"
        >
          <div class="flex flex-col">
            <BlInputText :input="nameInput" @changed="inputHandler" />
            <BlTextBody3
              text="Invited users can change their name after logging in."
            />
          </div>
          <BlInputText :input="emailInput" @changed="onInputEmail" />
          <BlSelectBox
            label="Role"
            asterisk
            v-model="roleInput"
            placeholder="Select role"
            :options="roleOptions"
            @change="roleInput = $event"
          />
          <div class="grid grid-cols-2 lg:flex items-center justify-end gap-4">
            <BlInputButton
              type="plain"
              label="Cancel"
              @clicked="$emit('close-modal')"
            />
            <BlInputButton
              label="Next"
              :disabled="!Step1Verified"
              buttonType="submit"
            />
          </div>
        </form>

        <div v-if="currentStep === 2" class="flex flex-col gap-4">
          <div class="flex flex-col">
            <BlTextSubtitle2 class="text-slate-1000" text="Set password" />
            <BlTextBody2
              text="Choose how to set the password for new members' accounts."
            />
          </div>
          <div
            class="flex gap-4 cursor-pointer"
            @click="passwordAutogenerated = true"
          >
            <BlInputRadio
              :checked="passwordAutogenerated"
              name="autogenerated"
            />
            <div class="flex flex-col gap-1">
              <BlTextSubtitle2 text="Autogenerated password" />
              <BlTextBody2
                text="You can view the password after you create the user."
              />
            </div>
          </div>
          <div
            class="flex gap-4 cursor-pointer"
            @click="passwordAutogenerated = false"
          >
            <BlInputRadio
              :checked="!passwordAutogenerated"
              name="autogenerated"
            />
            <div class="flex flex-col gap-1">
              <BlTextSubtitle2 text="Custom password" />
              <BlTextBody2 text="Enter a custom password for the user." />
            </div>
          </div>
          <div class="ml-8 flex flex-col gap-4">
            <BlInputText :input="customPasswordInput" @changed="inputHandler" />
            <div class="grid grid-cols-3">
              <div class="flex items-center gap-2">
                <BlIcon
                  icon="check"
                  :color="'secondary'"
                  class="rounded-full w-6 h-6"
                  :class="[
                    passwordHasSixChar ? 'bg-success-400' : 'bg-grey-300'
                  ]"
                />
                <div class="text-xs">6 characters minimum</div>
              </div>
              <div class="flex items-center gap-2">
                <BlIcon
                  icon="check"
                  :color="'secondary'"
                  class="rounded-full w-6 h-6"
                  :class="[
                    passwordHasUppercase ? 'bg-success-400' : 'bg-grey-300'
                  ]"
                />
                <div class="text-xs">1 uppercase character</div>
              </div>
            </div>
            <div class="grid grid-cols-3">
              <div class="flex items-center gap-2">
                <BlIcon
                  icon="check"
                  :color="'secondary'"
                  class="rounded-full w-6 h-6"
                  :class="[
                    passwordHasLowescase ? 'bg-success-400' : 'bg-grey-300'
                  ]"
                />
                <div class="text-xs">1 lowercase character</div>
              </div>
              <div class="flex items-center gap-2">
                <BlIcon
                  icon="check"
                  :color="'secondary'"
                  class="rounded-full w-6 h-6"
                  :class="[
                    passwordHasNumber ? 'bg-success-400' : 'bg-grey-300'
                  ]"
                />
                <div class="text-xs">1 number</div>
              </div>
            </div>
          </div>
          <div class="grid grid-cols-2 lg:flex items-center justify-end gap-4">
            <BlInputButton
              type="plain"
              label="Cancel"
              @clicked="$emit('close-modal')"
            />
            <BlInputButton
              type="outlined"
              label="Back"
              @clicked="currentStep = 1"
            />
            <BlInputButton
              label="Next"
              :disabled="!Step2Verified"
              @clicked="currentStep = 3"
            />
          </div>
        </div>

        <div v-if="currentStep === 3" class="flex flex-col gap-4">
          <div class="py-4 px-6 flex flex-col gap-4 rounded-3xl bg-grey-100">
            <div class="grid grid-cols-12">
              <BlTextBody2 class="col-span-2" text="Name" />
              <BlTextBody2
                class="text-slate-1000 font-medium col-span-10"
                :text="nameInput.model"
              />
            </div>
            <div class="grid grid-cols-12">
              <BlTextBody2 class="col-span-2" text="Email" />
              <BlTextBody2
                class="text-slate-1000 font-medium col-span-10"
                :text="emailInput.model"
              />
            </div>
            <div class="grid grid-cols-12">
              <BlTextBody2 class="col-span-2" text="Role" />
              <BlTextBody2
                class="text-slate-1000 font-medium col-span-10"
                :text="userRoles[roleInput]"
              />
            </div>
            <div class="grid grid-cols-12 items-center">
              <BlTextBody2 class="col-span-2" text="Password" />
              <div v-if="passwordAutogenerated" class="col-span-10">
                <BlTextBody2
                  class="text-slate-1000 font-medium"
                  text="Autogenerated"
                />
              </div>
              <div v-else class="col-span-10 flex items-center gap-2">
                <BlTextBody2
                  v-if="showPassword"
                  :text="customPasswordInput.model"
                  class="text-slate-1000 font-medium"
                />
                <BlTextBody2
                  v-if="!showPassword"
                  :text="customPasswordInput.model.replace(/[\S]/g, '*')"
                  class="text-slate-1000 font-medium"
                />
                <BlInputButton
                  type="plain"
                  :label="showPassword ? 'Hide' : 'Show'"
                  dense
                  @clicked="showPassword = !showPassword"
                />
              </div>
            </div>
          </div>
          <div class="grid grid-cols-2 lg:flex items-center justify-end gap-4">
            <BlInputButton
              type="plain"
              label="Cancel"
              @clicked="$emit('close-modal')"
            />
            <BlInputButton
              type="outlined"
              label="Back"
              @clicked="currentStep = 2"
            />
            <BlInputButton
              label="Create user"
              :loading="submitLoading"
              @clicked="onSubmit"
            />
          </div>
        </div>
      </div>
    </BlBackdrop>

    <BlBackdrop
      v-if="loginDetailsOpened"
      title="Send login details"
      description="Please be sure to save this information as it will not be accessible again once the pop-up is closed."
      :footer="false"
    >
      <div class="flex flex-col gap-4 pb-8 px-8">
        <div class="py-4 px-6 flex flex-col gap-4 rounded-3xl bg-slate-100">
          <div class="grid grid-cols-12 items-center">
            <BlTextBody2 class="col-span-2" text="Sign in URL" />
            <div class="col-span-10 flex items-center gap-2">
              <BlInputButton
                type="plain"
                icon="copy"
                @clicked="
                  copyToClipboard(
                    `${origin}/setup-account/${loginDetails.signInToken}`,
                    'Sign in URL'
                  )
                "
              />
              <BlTextBody2
                class="text-slate-1000 font-medium"
                :text="`${origin}/setup-account/${loginDetails.signInToken}`"
              />
            </div>
          </div>
          <div class="grid grid-cols-12 items-center">
            <BlTextBody2 class="col-span-2" text="Email" />
            <div class="col-span-10 flex items-center gap-2">
              <BlInputButton
                type="plain"
                icon="copy"
                @clicked="copyToClipboard(loginDetails.email, 'Email')"
              />
              <BlTextBody2
                class="text-slate-1000 font-medium"
                :text="loginDetails.email"
              />
            </div>
          </div>
          <div class="grid grid-cols-12 items-center">
            <BlTextBody2 class="col-span-2" text="Password" />
            <div class="col-span-10 flex items-center gap-2">
              <BlInputButton
                type="plain"
                icon="copy"
                @clicked="copyToClipboard(loginDetails.password, 'Password')"
              />
              <BlTextBody2
                v-if="!showPassword"
                :text="loginDetails.password.replace(/[\S]/g, '*')"
                class="text-slate-1000 font-medium"
              />
              <BlTextBody2
                v-if="showPassword"
                :text="loginDetails.password"
                class="text-slate-1000 font-medium"
              />
              <BlInputButton
                type="plain"
                :label="showPassword ? 'Hide' : 'Show'"
                dense
                @clicked="showPassword = !showPassword"
              />
            </div>
          </div>
        </div>

        <div class="flex items-center justify-end gap-4">
          <BlInputButton
            type="plain"
            label="Close"
            @clicked="$emit('close-modal')"
          />
          <BlInputButton
            type="outlined"
            label="Download details"
            :loading="downloadLoading"
            @clicked="onDownloadDetails"
          />
          <BlInputButton label="Send via email" @clicked="onSendViaEmail" />
        </div>
      </div>
    </BlBackdrop>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import debounce from "lodash/debounce";

import BlBackdrop from "../BlBackdrop.vue";
import BlTextSubtitle2 from "../BlTextSubtitle2.vue";
import BlTextSubtitle3 from "../BlTextSubtitle3.vue";
import BlTextBody2 from "../BlTextBody2.vue";
import BlTextBody3 from "../BlTextBody3.vue";
import BlInputText from "../BlInputText.vue";
import BlInputRadio from "../BlInputRadio.vue";
import BlSelectBox from "../BlSelectBox.vue";
import BlInputButton from "../BlInputButton.vue";
import BlIcon from "../BlIcon.vue";

export default {
  name: "NewUserModal",
  components: {
    BlBackdrop,
    BlTextSubtitle2,
    BlTextSubtitle3,
    BlTextBody2,
    BlTextBody3,
    BlInputText,
    BlInputRadio,
    BlSelectBox,
    BlInputButton,
    BlIcon
  },
  props: {
    centerId: { type: Number, required: true }
    // @close-modal
  },
  data() {
    return {
      submitLoading: false,
      currentStep: 1,
      invitationProgress: [
        { step: 1, label: "Fill identity" },
        { step: 2, label: "Set Password" },
        { step: 3, label: "Review and create member" }
      ],
      //
      nameInput: {
        label: "Name",
        placeholder: "Type the name",
        asterisk: true,
        model: ""
      },
      emailInput: {
        label: "Email",
        placeholder: "Type the email",
        asterisk: true,
        inputType: "email",
        error: "",
        success: "",
        model: ""
      },
      emailAvailable: false,
      roleInput: "",
      roleOptions: [
        { value: "admin", label: "Admin" },
        { value: "staff", label: "Therapist" }
      ],
      userRoles: {
        admin: "Admin",
        staff: "Therapist",
        super_admin: "Super admin"
      },
      //
      passwordAutogenerated: true,
      customPasswordInput: {
        placeholder: "Type the custom password",
        inputType: "password",
        disabled: true,
        model: ""
      },
      //
      origin: "",
      downloadLoading: false,
      showPassword: false,
      loginDetailsOpened: false,
      loginDetails: {
        signInToken: "",
        email: "",
        status: "",
        password: ""
      }
    };
  },
  computed: {
    ...mapGetters(["currentUser"]),
    Step1Verified() {
      return (
        this.nameInput.model &&
        this.emailInput.model &&
        this.emailAvailable &&
        this.roleInput
      );
    },
    Step2Verified() {
      return this.passwordAutogenerated || this.customPasswordVerified;
    },
    passwordHasSixChar() {
      return this.customPasswordInput.model.length >= 6;
    },
    passwordHasUppercase() {
      return /[A-Z]/.test(this.customPasswordInput.model);
    },
    passwordHasLowescase() {
      return /[a-z]/.test(this.customPasswordInput.model);
    },
    passwordHasNumber() {
      return /\d/.test(this.customPasswordInput.model);
    },
    customPasswordVerified() {
      return (
        this.passwordHasSixChar &&
        this.passwordHasUppercase &&
        this.passwordHasLowescase &&
        this.passwordHasNumber
      );
    }
  },
  watch: {
    passwordAutogenerated(val) {
      this.customPasswordInput.disabled = val;
    }
  },
  mounted() {
    this.origin = window.location.origin;
  },
  methods: {
    ...mapActions([
      "checkUserEmail",
      "createUser",
      "downloadUserDetails",
      "showToast"
    ]),
    inputHandler(payload) {
      payload.input.model = payload.value;
    },
    onInputEmail(payload) {
      payload.input.model = payload.value;
      this.onCheckEmail(payload.value);
    },
    onCheckEmail: debounce(async function(data) {
      const params = { email: data };
      const { available, message } = await this.checkUserEmail(params);
      if (available) {
        this.emailAvailable = true;
        this.emailInput.error = "";
        this.emailInput.success = message;
      } else {
        this.emailAvailable = false;
        this.emailInput.error = message;
        this.emailInput.success = "";
        this.showToast({ title: "", message, type: "error" });
      }
    }, 1000),
    async onSubmit() {
      const params = {
        center_id: this.centerId,
        user: {
          name: this.nameInput.model,
          email: this.emailInput.model,
          role: this.roleInput
        }
      };

      if (!this.passwordAutogenerated) {
        params.user.password = this.customPasswordInput.model;
        params.user.password_confirmation = this.customPasswordInput.model;
      }

      this.submitLoading = true;
      const { success, data } = await this.createUser(params);
      this.submitLoading = false;
      if (!success) return;

      this.loginDetails = {
        signInToken: data.user.sign_in_token,
        email: data.user.email,
        password: data.password
      };
      this.loginDetailsOpened = true;
    },
    //
    copyToClipboard(text, label = "Text") {
      navigator.clipboard.writeText(text);
      const title = `${label} has been copied to clipboard!`;
      this.showToast({ title, message: "", type: "success" });
    },
    async onDownloadDetails() {
      const { signInToken, email, password } = this.loginDetails;
      const params = `?email=${email}&password=${password}&sign_in_token=${signInToken}`;

      this.downloadLoading = true;
      const { response, success } = await this.downloadUserDetails(params);
      this.downloadLoading = false;
      if (!success) return;

      const title = "Downloading details";
      this.showToast({ title, message: "", type: "success" });

      const href = URL.createObjectURL(response.data);
      const link = document.createElement("a");

      let filename = "";
      const disposition = response.headers["content-disposition"];
      const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
      const matches = filenameRegex.exec(disposition);
      if (matches != null && matches[1]) {
        filename = matches[1].replace(/['"]/g, "");
      }

      link.href = href;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      URL.revokeObjectURL(href);
    },
    onSendViaEmail() {
      const { email, signInToken } = this.loginDetails;

      const subject = `Welcome to Blüm! Your Invitation Awaits`;
      let body = `
Hello.

We are excited to invite you to join Blüm. Click the link below to create your account and experience a new level of efficiency in your practice.

Sign-in URL: ${this.origin}/setup-account/${signInToken}

Your password will be provided separately by your admin.

If you encounter any issues during the registration process or have any questions about Blüm, feel free to reach out to our support team at blum@simpul.tech. We're here to assist you every step of the way.


Best regards,

Blüm Support Team
`;
      body = body.replaceAll("\\\\", "%5C");
      body = body.replaceAll(" ", "%20");
      body = body.replaceAll("\r", "%0D");
      body = body.replaceAll("\n", "%0A");
      body = body.replaceAll("\t", "%09");

      const anchor = document.createElement("a");
      anchor.setAttribute("target", "_blank");
      anchor.setAttribute(
        "href",
        `https://mail.google.com/mail/?view=cm&fs=1&to=${email}&su=${subject}&body=${body}`
      );
      anchor.click();
      setTimeout(() => {
        anchor.remove();
      }, 300);
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/team/NewUserModal.vue